import React, { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Globe, X } from 'lucide-react';
import { useDocumentationTranslations } from '@/hooks/useDocumentationTranslations';

interface LanguageFallbackBannerProps {
  requestedLanguage: string;
  fallbackLanguage: string;
  className?: string;
}

export function LanguageFallbackBanner({
  requestedLanguage,
  fallbackLanguage,
  className = '',
}: LanguageFallbackBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const { getFallbackLabels, getFallbackText, getLanguageName } = useDocumentationTranslations();

  // Don't show banner if using the fallback language or if dismissed
  if (requestedLanguage === fallbackLanguage || dismissed) {
    return null;
  }

  const requestedLanguageName = getLanguageName(requestedLanguage);
  const fallbackLanguageName = getLanguageName(fallbackLanguage);

  return (
    <Alert
      className={`border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 ${className}`}
    >
      <Globe className="size-4 text-amber-600 dark:text-amber-400" />
      <AlertDescription className="flex w-full items-center justify-between">
        <div className="text-amber-800 dark:text-amber-200">
          <span className="font-medium">{getFallbackLabels.title}</span>
          <span className="ml-2">
            {getFallbackText('requestedInLanguage', {
              requestedLanguage: requestedLanguageName,
            })}{' '}
            {getFallbackText('showingFallback', {
              fallbackLanguage: fallbackLanguageName,
            })}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDismissed(true)}
          className="ml-4 size-6 p-0 text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200"
          aria-label={getFallbackLabels.dismiss}
        >
          <X className="size-3" />
        </Button>
      </AlertDescription>
    </Alert>
  );
}
